import {
  useNavigation,
  useSearchParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ActiveFilterChips } from "@/components/gsc/ActiveFilterChips";
import { DomainOverviewTable } from "@/components/gsc/DomainOverviewTable";
import { GscFiltersPanel } from "@/components/gsc/GscFiltersPanel";
import { GscSubNav } from "@/components/gsc/GscSubNav";
import { SummaryCards } from "@/components/gsc/SummaryCards";
import { TrendChart } from "@/components/gsc/TrendChart";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { queryClient } from "@/lib/queryClient";
import {
  EMPTY_GSC_DOMAIN_OVERVIEW_RESULT,
  EMPTY_GSC_FILTER_OPTIONS,
  EMPTY_GSC_KPI_SUMMARY,
  type GscDomainOverviewSortColumn,
  type GscTrendGranularity,
} from "@/types/gsc";
import { isGscTrendMetric, type GscTrendMetric } from "@/lib/gsc-trend";
import {
  fetchGscDomainOverview,
  fetchGscKpi,
  fetchGscTrend,
  getGscDomainOverviewQueryKey,
  getGscKpiQueryKey,
  getGscTrendQueryKey,
  GSC_STATS_STALE_TIME,
  parseGscDomainOverviewParamsFromUrl,
  parseGscKpiParamsFromUrl,
  parseGscTrendParamsFromUrl,
  useGscDomainOverview,
  useGscFilterOptions,
  useGscKpi,
  useGscTrend,
  type GscDomainOverviewParams,
  type GscKpiParams,
  type GscTrendParams,
} from "@/hooks/useGscData";

export interface GscOverviewLoaderData {
  kpiParams: GscKpiParams;
  trendParams: GscTrendParams;
  domainOverviewParams: GscDomainOverviewParams;
}

const createUrlFromSearch = (search: string) =>
  new URL(`https://local?${search}`);

export async function gscOverviewLoader({
  request,
}: LoaderFunctionArgs): Promise<GscOverviewLoaderData> {
  const url = new URL(request.url);
  const kpiParams = parseGscKpiParamsFromUrl(url);
  const trendParams = parseGscTrendParamsFromUrl(url);
  const domainOverviewParams = parseGscDomainOverviewParamsFromUrl(url);

  // Filter options are deliberately NOT prefetched here: they only fill the
  // dropdowns, and their query (a country scan over fact_gsc_data) can be slow /
  // time out. Awaiting it would stall the whole page behind it; the component
  // fetches them via useGscFilterOptions and soft-fails to empty options.
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: getGscKpiQueryKey(kpiParams),
      queryFn: () => fetchGscKpi(kpiParams),
      staleTime: GSC_STATS_STALE_TIME,
    }),
    queryClient.prefetchQuery({
      queryKey: getGscTrendQueryKey(trendParams),
      queryFn: () => fetchGscTrend(trendParams),
      staleTime: GSC_STATS_STALE_TIME,
    }),
    queryClient.prefetchQuery({
      queryKey: getGscDomainOverviewQueryKey(domainOverviewParams),
      queryFn: () => fetchGscDomainOverview(domainOverviewParams),
      staleTime: GSC_STATS_STALE_TIME,
    }),
  ]);

  return { kpiParams, trendParams, domainOverviewParams };
}

export default function GscOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useNavigation();

  const url = useMemo(
    () => createUrlFromSearch(searchParams.toString()),
    [searchParams],
  );
  const kpiParams = useMemo(() => parseGscKpiParamsFromUrl(url), [url]);
  const trendParams = useMemo(() => parseGscTrendParamsFromUrl(url), [url]);
  const domainOverviewParams = useMemo(
    () => parseGscDomainOverviewParamsFromUrl(url),
    [url],
  );
  const metric = useMemo((): GscTrendMetric => {
    const raw = searchParams.get("metric") ?? "clicks";
    return isGscTrendMetric(raw) ? raw : "clicks";
  }, [searchParams]);

  const {
    data: kpi,
    isPending: isKpiPending,
    isFetching: isKpiFetching,
    isError: isKpiError,
  } = useGscKpi(kpiParams);
  const {
    data: trend,
    isPending: isTrendPending,
    isFetching: isTrendFetching,
    isError: isTrendError,
  } = useGscTrend(trendParams);
  const {
    data: domainOverview,
    isPending: isDomainOverviewPending,
    isFetching: isDomainOverviewFetching,
    isSuccess: isDomainOverviewSuccess,
    isError: isDomainOverviewError,
  } = useGscDomainOverview(domainOverviewParams);
  const {
    data: filterOptions,
    isPending: isFilterOptionsPending,
    isError: isFilterOptionsError,
  } = useGscFilterOptions();

  // Out-of-range ovPage → empty page: clamp back to page 1.
  useEffect(() => {
    if (
      !isDomainOverviewSuccess ||
      !domainOverview ||
      domainOverview.rows.length > 0 ||
      domainOverviewParams.page <= 1
    ) {
      return;
    }

    setSearchParams((prev) => {
      if (!prev.has("ovPage")) return prev;
      const next = new URLSearchParams(prev);
      next.delete("ovPage");
      return next;
    });
  }, [
    domainOverview,
    domainOverviewParams.page,
    isDomainOverviewSuccess,
    setSearchParams,
  ]);

  const handleMetricChange = useCallback(
    (nextMetric: GscTrendMetric) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextMetric === "clicks") {
          next.delete("metric");
        } else {
          next.set("metric", nextMetric);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleGranularityChange = useCallback(
    (granularity: GscTrendGranularity) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("granularity", granularity);
        return next;
      });
    },
    [setSearchParams],
  );

  const handleDomainsChange = useCallback(
    (domains: string[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("domains");
        next.delete("domain");
        if (domains.length > 0) {
          next.set("domains", domains.join(","));
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleOverviewPageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) {
          next.delete("ovPage");
        } else {
          next.set("ovPage", String(page));
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleOverviewSortChange = useCallback(
    (column: GscDomainOverviewSortColumn) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentSort = domainOverviewParams.sortBy;
        const currentDir = domainOverviewParams.sortDir;

        if (column === currentSort) {
          const nextDir = currentDir === "asc" ? "desc" : "asc";
          if (column === "clicks" && nextDir === "desc") {
            next.delete("sortBy");
            next.delete("sortDir");
          } else {
            if (column === "clicks") {
              next.delete("sortBy");
            } else {
              next.set("sortBy", column);
            }
            if (nextDir === "desc") {
              next.delete("sortDir");
            } else {
              next.set("sortDir", nextDir);
            }
          }
        } else {
          const nextDir = column === "domain" ? "asc" : "desc";
          if (column === "clicks") {
            next.delete("sortBy");
          } else {
            next.set("sortBy", column);
          }
          if (nextDir === "desc") {
            next.delete("sortDir");
          } else {
            next.set("sortDir", nextDir);
          }
        }

        next.delete("ovPage");
        return next;
      });
    },
    [
      domainOverviewParams.sortBy,
      domainOverviewParams.sortDir,
      setSearchParams,
    ],
  );

  const selectedDomains = trendParams.trendDomains;

  const isRouteLoading = navigation.state === "loading";
  const hasKpiData = kpi !== undefined;
  const hasTrendData = trend !== undefined;
  const hasDomainOverviewData = domainOverview !== undefined;
  const showSummarySkeleton = (isRouteLoading || isKpiPending) && !hasKpiData;
  const isSummaryRefetching = isKpiFetching && hasKpiData;
  const showTrendSkeleton = (isRouteLoading || isTrendPending) && !hasTrendData;
  const isTrendRefetching = isTrendFetching && hasTrendData;
  const showDomainOverviewSkeleton =
    (isRouteLoading || isDomainOverviewPending) && !hasDomainOverviewData;
  const isDomainOverviewRefetching =
    isDomainOverviewFetching && hasDomainOverviewData;
  const summary = kpi ?? EMPTY_GSC_KPI_SUMMARY;
  const overviewResult = domainOverview ?? EMPTY_GSC_DOMAIN_OVERVIEW_RESULT;
  const hasLoadError = isKpiError || isTrendError || isDomainOverviewError;

  // Loud, visible errors: the inline alert/note alone was too easy to miss, so a
  // failed query also raises a red toast. A stable `id` per error replaces (not
  // stacks) the toast when the same failure re-fires on refetch.
  useEffect(() => {
    if (hasLoadError) {
      toast.error("Couldn't load GSC data", {
        id: "gsc-load-error",
        description: "Some panels failed to load — try again or narrow the date range.",
      });
    }
  }, [hasLoadError]);
  useEffect(() => {
    if (isFilterOptionsError) {
      toast.error("GSC filters unavailable", {
        id: "gsc-filters-error",
        description: "Filter dropdowns are empty; the rest of the page still works.",
      });
    }
  }, [isFilterOptionsError]);

  // Source of truth: get_gsc_filter_options.domains (normalized → .domain) + URL selection.
  const availableDomains = useMemo(() => {
    const fromFilterOptions =
      filterOptions?.domain ?? EMPTY_GSC_FILTER_OPTIONS.domain;
    const fromUrl = selectedDomains ?? [];
    return [...new Set([...fromFilterOptions, ...fromUrl])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [filterOptions?.domain, selectedDomains]);

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">GSC Overview</h1>
        <p className="text-sm text-muted-foreground">
          Search Console performance across scoped domains.
        </p>
      </div>

      <GscSubNav />

      <div>
        <GscFiltersPanel
          options={filterOptions ?? EMPTY_GSC_FILTER_OPTIONS}
          isLoading={isFilterOptionsPending}
        />
        {isFilterOptionsError ? (
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertTriangle className="size-3.5 text-amber-500" />
            Filter options are temporarily unavailable — dropdowns may be empty. The
            rest of the page is unaffected.
          </p>
        ) : null}
        <ActiveFilterChips />
      </div>

      {hasLoadError ? (
        <Card className="border-border shadow-none">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>Failed to load GSC data</AlertTitle>
              <AlertDescription>
                Please try again. If the problem persists, check your connection
                or refresh the page.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <>
          <SummaryCards
            data={summary}
            isLoading={showSummarySkeleton}
            isFetching={isSummaryRefetching}
          />

          <TrendChart
            points={trend ?? []}
            metric={metric}
            granularity={trendParams.granularity}
            selectedDomains={selectedDomains}
            availableDomains={availableDomains}
            isLoading={showTrendSkeleton}
            isFetching={isTrendRefetching}
            onMetricChange={handleMetricChange}
            onGranularityChange={handleGranularityChange}
            onSelectedDomainsChange={handleDomainsChange}
          />

          <DomainOverviewTable
            rows={overviewResult.rows}
            totalCount={overviewResult.totalCount}
            page={domainOverviewParams.page}
            pageSize={domainOverviewParams.pageSize}
            sortBy={domainOverviewParams.sortBy}
            sortDir={domainOverviewParams.sortDir}
            availableDomains={availableDomains}
            onPageChange={handleOverviewPageChange}
            onSortChange={handleOverviewSortChange}
            isLoading={showDomainOverviewSkeleton}
            isFetching={isDomainOverviewRefetching}
          />
        </>
      )}
    </div>
  );
}
